import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import logoSrc from "@/assets/logo.png";

export const Route = createFileRoute("/update-password")({
  head: () => ({ meta: [{ title: "Update Password — Code Cubs Academy" }] }),
  component: UpdatePasswordPage,
});

const schema = z
  .object({
    password: z.string().min(6).max(100),
    confirmPassword: z.string().min(6).max(100),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

function UpdatePasswordPage() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Wait for supabase to process the hash or PKCE code in the URL
    // before we allow the user to submit the form.
    const checkAuth = async () => {
      // Supabase handles the code exchange automatically in the background.
      // We just need to wait for the session to be populated.
      if (!loading) {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, [loading]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!session) {
      toast.error("You must be logged in to update your password. The link may have expired.");
      return navigate({ to: "/login" });
    }

    const fd = new FormData(e.currentTarget);
    const parsed = schema.safeParse({
      password: String(fd.get("password") ?? ""),
      confirmPassword: String(fd.get("confirmPassword") ?? ""),
    });
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);

    setBusy(true);
    const { error } = await supabase.auth.updateUser({
      password: parsed.data.password,
    });
    setBusy(false);

    if (error) {
      return toast.error(error.message);
    }

    toast.success("Password updated successfully!");
    navigate({ to: "/dashboard" });
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card className="rounded-3xl border-0 shadow-soft">
        <CardContent className="p-8">
          <div className="flex justify-center mb-6">
            <img src={logoSrc} alt="Code Cubs Academy" className="h-32 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-extrabold text-center mb-6">Set New Password 🔐</h1>

          {!authChecked || loading ? (
            <div className="flex flex-col items-center justify-center space-y-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Verifying link...</p>
            </div>
          ) : !session ? (
            <div className="text-center space-y-4 py-4">
              <p className="text-red-500 font-medium">Invalid or expired recovery link.</p>
              <Button
                onClick={() => navigate({ to: "/forgot-password" })}
                variant="outline"
                className="w-full"
              >
                Request new link
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password (6+ chars)</Label>
                <Input id="password" name="password" type="password" required minLength={6} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  minLength={6}
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full rounded-full h-12 bg-gradient-primary text-primary-foreground border-0 mt-4"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
